/**
 * BundleConfigEditor
 * ------------------
 * Admin editor for `menu_items.bundle_config`. Rendered inside
 * MenuItemDialog when the admin picks Product Type = "Meal Deal / Bundle".
 *
 * Everything here is a pure controlled component driven by `value` and
 * `onChange`. It never talks to the API directly (except for fetching the
 * catalogue of products / categories / modifier groups it needs to
 * populate its own dropdowns).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Copy as CopyIcon,
  Eye,
  Plus,
  Trash2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { bundleAPI, menuAPI } from '../../utils/api';
import { BundleBuilderModal } from '../custom/BundleBuilderModal';

const uid = () => `bnd_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const emptyGroup = () => ({
  id: uid(),
  title: 'New Selection',
  instructions: '',
  required: true,
  min_selections: 1,
  max_selections: 1,
  quantity: 1,
  selection_type: 'single',
  // Sensible default for repeat-per-person groups (meal deals): two people
  // ordering the same wrap is a common and legal case.
  allow_duplicates: true,
  repeat_per_person: false,
  person_count: 1,
  person_labels: ['Person 1'],
  display_order: 0,
  source: { kind: 'menu_items', menu_item_ids: [], category_names: [], custom_options: [], modifier_group_id: null },
  options: [],
});

const emptyOption = () => ({
  id: uid(),
  source_ref: null,
  display_name_override: null,
  pricing_mode: 'included',
  surcharge: 0,
  allowance: 0,
  active: true,
  nested_modifier_group_ids: [],
});

const emptyFixedItem = () => ({
  id: uid(),
  item_ref: null,
  custom_name: 'Included item',
  quantity: 1,
});

const defaultBundleConfig = () => ({
  feeds_label: '',
  kitchen_name: '',
  max_qty_per_order: 10,
  available_days: [0, 1, 2, 3, 4, 5, 6],
  available_from: '',
  available_until: '',
  order_types: ['collection', 'delivery'],
  fixed_items: [],
  groups: [],
});

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const PRICING_MODES = [
  { value: 'included', label: 'Included in base price' },
  { value: 'fixed_surcharge', label: 'Fixed surcharge (+£)' },
  { value: 'fixed_discount', label: 'Fixed discount (-£)' },
  { value: 'use_menu_price', label: 'Charge the item’s menu price' },
  { value: 'above_allowance', label: 'Charge above allowance' },
];

export const BundleConfigEditor = ({ value, onChange, bundlePreview }) => {
  const config = value || defaultBundleConfig();
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [modifierGroups, setModifierGroups] = useState([]);
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Fetch reference data
  useEffect(() => {
    (async () => {
      try {
        const [m, c, mg] = await Promise.all([
          menuAPI.getAllMenuItems(),
          menuAPI.getAllCategories(),
          bundleAPI.listModifierGroups(true),
        ]);
        setMenuItems(m.data || []);
        setCategories(c.data || []);
        setModifierGroups(mg.data || []);
      } catch (err) {
        // Silent — editor is still usable, just won't autofill lookups.
        console.error('BundleConfigEditor: failed to load references', err);
      }
    })();
  }, []);

  const set = (patch) => onChange({ ...config, ...patch });

  const productsById = useMemo(() => {
    const map = {};
    menuItems.forEach((m) => (map[m.id] = m));
    return map;
  }, [menuItems]);

  // ---- Fixed items --------------------------------------------------------
  const updateFixedItem = (id, patch) => {
    set({
      fixed_items: (config.fixed_items || []).map((fx) =>
        fx.id === id ? { ...fx, ...patch } : fx,
      ),
    });
  };
  const removeFixedItem = (id) => {
    set({ fixed_items: (config.fixed_items || []).filter((fx) => fx.id !== id) });
  };
  const addFixedItem = () => {
    set({ fixed_items: [...(config.fixed_items || []), emptyFixedItem()] });
  };

  // ---- Groups -------------------------------------------------------------
  const updateGroup = (id, patch) => {
    set({
      groups: (config.groups || []).map((g) => (g.id === id ? { ...g, ...patch } : g)),
    });
  };
  const removeGroup = (id) => {
    set({ groups: (config.groups || []).filter((g) => g.id !== id) });
  };
  const addGroup = () => {
    const g = { ...emptyGroup(), display_order: (config.groups || []).length };
    set({ groups: [...(config.groups || []), g] });
    setExpandedGroupId(g.id);
  };
  const moveGroup = (id, dir) => {
    const arr = [...(config.groups || [])];
    const idx = arr.findIndex((g) => g.id === id);
    if (idx < 0) return;
    const swap = dir === 'up' ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= arr.length) return;
    [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
    arr.forEach((g, i) => (g.display_order = i));
    set({ groups: arr });
  };

  // ---- Group source -------------------------------------------------------
  const setGroupSource = (gid, source) => updateGroup(gid, { source });

  const addOptionToGroup = (gid, option = emptyOption()) => {
    const g = (config.groups || []).find((x) => x.id === gid);
    if (!g) return;
    updateGroup(gid, { options: [...(g.options || []), option] });
  };
  const removeOptionFromGroup = (gid, oid) => {
    const g = (config.groups || []).find((x) => x.id === gid);
    if (!g) return;
    updateGroup(gid, { options: (g.options || []).filter((o) => o.id !== oid) });
  };
  const updateOptionInGroup = (gid, oid, patch) => {
    const g = (config.groups || []).find((x) => x.id === gid);
    if (!g) return;
    updateGroup(gid, {
      options: (g.options || []).map((o) => (o.id === oid ? { ...o, ...patch } : o)),
    });
  };

  const bulkAddProductsAsOptions = (gid, productIds, defaultMode = 'included') => {
    const g = (config.groups || []).find((x) => x.id === gid);
    if (!g) return;
    const existing = new Set((g.options || []).map((o) => o.source_ref));
    const newOptions = productIds
      .filter((pid) => !existing.has(pid))
      .map((pid) => ({
        ...emptyOption(),
        source_ref: pid,
        pricing_mode: defaultMode,
      }));
    if (!newOptions.length) return;
    updateGroup(gid, { options: [...(g.options || []), ...newOptions] });
  };

  const togglePersonLabelForRepeat = (gid, personCount) => {
    const labels = [];
    for (let i = 0; i < personCount; i += 1) labels.push(`Person ${i + 1}`);
    updateGroup(gid, { person_count: personCount, person_labels: labels });
  };

  return (
    <div className="space-y-6 border-t pt-6 mt-6" data-testid="bundle-config-editor">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Bundle Configuration</h3>
        {bundlePreview && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-full"
            onClick={() => setPreviewOpen(true)}
            data-testid="bundle-preview-btn"
          >
            <Eye className="h-4 w-4 mr-1.5" />
            Preview builder
          </Button>
        )}
      </div>

      {/* Bundle info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label>Feeds label</Label>
          <Input
            data-testid="bundle-feeds-label"
            value={config.feeds_label || ''}
            onChange={(e) => set({ feeds_label: e.target.value })}
            placeholder="e.g. Feeds 2 people"
          />
        </div>
        <div>
          <Label>Kitchen / receipt name</Label>
          <Input
            data-testid="bundle-kitchen-name"
            value={config.kitchen_name || ''}
            onChange={(e) => set({ kitchen_name: e.target.value })}
            placeholder="e.g. MEAL FOR 2"
          />
        </div>
        <div>
          <Label>Max quantity per order</Label>
          <Input
            data-testid="bundle-max-qty"
            type="number"
            min={1}
            value={config.max_qty_per_order}
            onChange={(e) => set({ max_qty_per_order: Math.max(1, Number(e.target.value) || 1) })}
          />
        </div>
        <div>
          <Label>Order types</Label>
          <div className="flex gap-3 mt-1.5">
            {['collection', 'delivery'].map((ot) => (
              <label key={ot} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={(config.order_types || []).includes(ot)}
                  onChange={(e) => {
                    const next = new Set(config.order_types || []);
                    if (e.target.checked) next.add(ot);
                    else next.delete(ot);
                    set({ order_types: Array.from(next) });
                  }}
                />
                {ot.charAt(0).toUpperCase() + ot.slice(1)}
              </label>
            ))}
          </div>
        </div>
        <div className="md:col-span-2">
          <Label>Available days</Label>
          <div className="flex gap-2 mt-1.5 flex-wrap">
            {DAY_LABELS.map((label, i) => {
              const on = (config.available_days || []).includes(i);
              return (
                <Button
                  key={label}
                  type="button"
                  size="sm"
                  variant={on ? 'default' : 'outline'}
                  className="rounded-full"
                  data-testid={`bundle-day-${i}`}
                  onClick={() => {
                    const set2 = new Set(config.available_days || []);
                    if (on) set2.delete(i);
                    else set2.add(i);
                    set({ available_days: Array.from(set2).sort() });
                  }}
                >
                  {label}
                </Button>
              );
            })}
          </div>
        </div>
        <div>
          <Label>Available from (HH:MM)</Label>
          <Input
            type="time"
            value={config.available_from || ''}
            onChange={(e) => set({ available_from: e.target.value })}
          />
        </div>
        <div>
          <Label>Available until (HH:MM)</Label>
          <Input
            type="time"
            value={config.available_until || ''}
            onChange={(e) => set({ available_until: e.target.value })}
          />
        </div>
      </div>

      {/* Fixed included items */}
      <div className="bg-secondary/40 rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-medium text-sm">Fixed included items (no customer choice)</h4>
          <Button type="button" size="sm" variant="outline" onClick={addFixedItem} data-testid="add-fixed-item">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add
          </Button>
        </div>
        {(config.fixed_items || []).length === 0 && (
          <p className="text-xs text-muted-foreground">No fixed items yet. Add e.g. &quot;10 Onion Rings&quot; or &quot;20 Plain Churros&quot;.</p>
        )}
        {(config.fixed_items || []).map((fx) => (
          <div key={fx.id} className="flex gap-2 items-end mt-2">
            <div className="flex-1">
              <Label className="text-xs">Name</Label>
              <Input
                data-testid="fixed-item-name"
                value={fx.custom_name}
                onChange={(e) => updateFixedItem(fx.id, { custom_name: e.target.value })}
                placeholder="10 Onion Rings"
              />
            </div>
            <div className="w-24">
              <Label className="text-xs">Qty</Label>
              <Input
                data-testid="fixed-item-qty"
                type="number"
                min={1}
                value={fx.quantity}
                onChange={(e) => updateFixedItem(fx.id, { quantity: Math.max(1, Number(e.target.value) || 1) })}
              />
            </div>
            <div className="w-56">
              <Label className="text-xs">Link to product (optional)</Label>
              <Select
                value={fx.item_ref || 'none'}
                onValueChange={(v) => updateFixedItem(fx.id, { item_ref: v === 'none' ? null : v })}
              >
                <SelectTrigger><SelectValue placeholder="No linked product" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No linked product</SelectItem>
                  {menuItems.filter((m) => (m.item_type || 'standard') === 'standard').map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => removeFixedItem(fx.id)}
            >
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          </div>
        ))}
      </div>

      {/* Selection groups */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-medium">Selection groups</h4>
          <Button type="button" size="sm" onClick={addGroup} data-testid="add-bundle-group">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add group
          </Button>
        </div>
        {(config.groups || []).length === 0 && (
          <p className="text-sm text-muted-foreground">
            No selection groups yet. Add one to let customers pick a wrap, fries, drink etc.
          </p>
        )}
        <div className="space-y-3">
          {(config.groups || []).map((g, idx) => (
            <GroupCard
              key={g.id}
              group={g}
              index={idx}
              expanded={expandedGroupId === g.id}
              onToggle={() => setExpandedGroupId(expandedGroupId === g.id ? null : g.id)}
              onUpdate={(patch) => updateGroup(g.id, patch)}
              onRemove={() => removeGroup(g.id)}
              onMove={(dir) => moveGroup(g.id, dir)}
              onSetSource={(source) => setGroupSource(g.id, source)}
              onAddOption={(opt) => addOptionToGroup(g.id, opt)}
              onRemoveOption={(oid) => removeOptionFromGroup(g.id, oid)}
              onUpdateOption={(oid, patch) => updateOptionInGroup(g.id, oid, patch)}
              onBulkAdd={(productIds, mode) => bulkAddProductsAsOptions(g.id, productIds, mode)}
              onSetPersonCount={(n) => togglePersonLabelForRepeat(g.id, n)}
              menuItems={menuItems}
              productsById={productsById}
              categories={categories}
              modifierGroups={modifierGroups}
            />
          ))}
        </div>
      </div>

      {previewOpen && bundlePreview && (
        <BundleBuilderModal
          bundle={bundlePreview}
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          onAdd={() => setPreviewOpen(false)}
        />
      )}
    </div>
  );
};

// -------------------------------------------------------------------------
// Group card
// -------------------------------------------------------------------------
const GroupCard = ({
  group,
  index,
  expanded,
  onToggle,
  onUpdate,
  onRemove,
  onMove,
  onSetSource,
  onAddOption,
  onRemoveOption,
  onUpdateOption,
  onBulkAdd,
  onSetPersonCount,
  menuItems,
  productsById,
  categories,
  modifierGroups,
}) => {
  const source = group.source || { kind: 'menu_items' };
  const [bulkPickerOpen, setBulkPickerOpen] = useState(false);

  return (
    <div className="border rounded-lg bg-white overflow-hidden" data-testid="bundle-group-card">
      <div className="flex items-center justify-between p-3 bg-gray-50">
        <button type="button" className="flex items-center gap-2 flex-1 min-w-0 text-left" onClick={onToggle}>
          <span className="text-xs text-muted-foreground font-mono w-6">#{index + 1}</span>
          <span className="font-medium truncate">{group.title || 'Untitled group'}</span>
          {group.required && (
            <span className="text-[10px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">Required</span>
          )}
          {group.repeat_per_person && (
            <span className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded-full">
              Per person × {group.person_count}
            </span>
          )}
        </button>
        <div className="flex items-center gap-1">
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => onMove('up')}>
            <ChevronUp className="h-4 w-4" />
          </Button>
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => onMove('down')}>
            <ChevronDown className="h-4 w-4" />
          </Button>
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={onRemove}>
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      </div>
      {expanded && (
        <div className="p-3 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Group title (customer-facing)</Label>
              <Input
                data-testid="group-title-input"
                value={group.title}
                onChange={(e) => onUpdate({ title: e.target.value })}
                placeholder="e.g. Person 1: Choose your wrap"
              />
            </div>
            <div>
              <Label>Instructions (optional)</Label>
              <Input
                value={group.instructions || ''}
                onChange={(e) => onUpdate({ instructions: e.target.value })}
              />
            </div>
            <div>
              <Label>Selection type</Label>
              <Select value={group.selection_type} onValueChange={(v) => onUpdate({ selection_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Single choice (radio)</SelectItem>
                  <SelectItem value="multiple">Multiple choice (checkbox)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Required</Label>
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  checked={!!group.required}
                  onChange={(e) => onUpdate({ required: e.target.checked })}
                />
                <span className="text-sm text-muted-foreground">Customer must pick from this group</span>
              </div>
            </div>
            <div>
              <Label>Min selections</Label>
              <Input
                type="number"
                min={0}
                value={group.min_selections}
                onChange={(e) => onUpdate({ min_selections: Math.max(0, Number(e.target.value) || 0) })}
              />
            </div>
            <div>
              <Label>Max selections</Label>
              <Input
                type="number"
                min={1}
                value={group.max_selections}
                onChange={(e) => onUpdate({ max_selections: Math.max(1, Number(e.target.value) || 1) })}
              />
            </div>
            <div>
              <Label>Allow duplicate choices across people</Label>
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  checked={!!group.allow_duplicates}
                  onChange={(e) => onUpdate({ allow_duplicates: e.target.checked })}
                />
                <span className="text-sm text-muted-foreground">e.g. two people can both choose the same wrap</span>
              </div>
            </div>
            <div>
              <Label>Repeat per person</Label>
              <div className="flex items-center gap-2 mt-2">
                <input
                  data-testid="repeat-per-person-toggle"
                  type="checkbox"
                  checked={!!group.repeat_per_person}
                  onChange={(e) => onUpdate({ repeat_per_person: e.target.checked })}
                />
                <span className="text-sm text-muted-foreground">Show this group once per person</span>
              </div>
            </div>
            {group.repeat_per_person && (
              <>
                <div>
                  <Label>Number of people</Label>
                  <Input
                    data-testid="person-count-input"
                    type="number"
                    min={1}
                    value={group.person_count}
                    onChange={(e) => onSetPersonCount(Math.max(1, Number(e.target.value) || 1))}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Person labels (one per line)</Label>
                  <Textarea
                    rows={3}
                    value={(group.person_labels || []).join('\n')}
                    onChange={(e) =>
                      onUpdate({
                        person_labels: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean),
                      })
                    }
                  />
                </div>
              </>
            )}
          </div>

          {/* Source picker */}
          <div className="border-t pt-3">
            <Label>Options come from</Label>
            <Select value={source.kind} onValueChange={(v) => onSetSource({ ...source, kind: v })}>
              <SelectTrigger data-testid="source-kind-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="menu_items">Specific menu products</SelectItem>
                <SelectItem value="categories">All active products in categories</SelectItem>
                <SelectItem value="custom">Custom options (name only)</SelectItem>
                <SelectItem value="modifier_group">Reusable modifier group</SelectItem>
              </SelectContent>
            </Select>

            {source.kind === 'categories' && (
              <div className="mt-2 flex flex-wrap gap-1">
                {categories.map((c) => {
                  const on = (source.category_names || []).includes(c.name);
                  return (
                    <Button
                      key={c.id}
                      type="button"
                      size="sm"
                      variant={on ? 'default' : 'outline'}
                      className="rounded-full text-xs"
                      onClick={() => {
                        const next = new Set(source.category_names || []);
                        if (on) next.delete(c.name);
                        else next.add(c.name);
                        onSetSource({ ...source, category_names: Array.from(next) });
                      }}
                    >
                      {c.name}
                    </Button>
                  );
                })}
              </div>
            )}

            {source.kind === 'modifier_group' && (
              <div className="mt-2">
                <Select
                  value={source.modifier_group_id || ''}
                  onValueChange={(v) => onSetSource({ ...source, modifier_group_id: v })}
                >
                  <SelectTrigger><SelectValue placeholder="Pick a modifier group" /></SelectTrigger>
                  <SelectContent>
                    {modifierGroups.map((mg) => (
                      <SelectItem key={mg.id} value={mg.id}>{mg.title || mg.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Options list */}
          <div className="border-t pt-3">
            <div className="flex items-center justify-between mb-2">
              <Label>Eligible options</Label>
              <div className="flex gap-2">
                {source.kind === 'menu_items' && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setBulkPickerOpen(true)}
                    data-testid="bulk-add-open"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add products
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => onAddOption(emptyOption())}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add empty option
                </Button>
              </div>
            </div>
            {(group.options || []).length === 0 ? (
              <p className="text-xs text-muted-foreground">
                {source.kind === 'categories'
                  ? 'Category source: options are auto-generated at customer runtime from all active products in the selected categories. You can still add explicit rows here to override pricing per product.'
                  : 'No options yet.'}
              </p>
            ) : (
              <div className="space-y-2">
                {(group.options || []).map((opt) => (
                  <OptionRow
                    key={opt.id}
                    opt={opt}
                    onUpdate={(patch) => onUpdateOption(opt.id, patch)}
                    onRemove={() => onRemoveOption(opt.id)}
                    source={source}
                    menuItems={menuItems}
                    productsById={productsById}
                    modifierGroups={modifierGroups}
                  />
                ))}
              </div>
            )}

            {bulkPickerOpen && (
              <BulkProductPicker
                menuItems={menuItems.filter((m) => (m.item_type || 'standard') === 'standard')}
                already={new Set((group.options || []).map((o) => o.source_ref))}
                onClose={() => setBulkPickerOpen(false)}
                onAdd={(ids, mode, surcharge) => {
                  const g = group;
                  const existing = new Set((g.options || []).map((o) => o.source_ref));
                  const added = ids
                    .filter((pid) => !existing.has(pid))
                    .map((pid) => ({
                      ...emptyOption(),
                      source_ref: pid,
                      pricing_mode: mode,
                      surcharge: mode === 'fixed_surcharge' || mode === 'fixed_discount' ? surcharge : 0,
                    }));
                  onUpdate({ options: [...(g.options || []), ...added] });
                  setBulkPickerOpen(false);
                }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const OptionRow = ({ opt, onUpdate, onRemove, source, menuItems, productsById, modifierGroups }) => {
  const displayName =
    opt.display_name_override ||
    (opt.source_ref && productsById[opt.source_ref]?.name) ||
    (source.kind === 'custom'
      ? (source.custom_options || []).find((c) => c.id === opt.source_ref)?.name
      : null) ||
    'Untitled option';
  return (
    <div className="border rounded-md p-2 bg-gray-50/50">
      <div className="flex items-center justify-between mb-1">
        <div className="font-medium text-sm truncate">{displayName}</div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-xs">
            <input
              type="checkbox"
              checked={opt.active !== false}
              onChange={(e) => onUpdate({ active: e.target.checked })}
            />
            Active
          </label>
          <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={onRemove}>
            <Trash2 className="h-3.5 w-3.5 text-red-500" />
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {source.kind === 'menu_items' && (
          <div>
            <Label className="text-xs">Menu product</Label>
            <Select
              value={opt.source_ref || ''}
              onValueChange={(v) => onUpdate({ source_ref: v })}
            >
              <SelectTrigger><SelectValue placeholder="Pick product…" /></SelectTrigger>
              <SelectContent>
                {menuItems.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.name} — £{Number(m.price).toFixed(2)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {source.kind === 'custom' && (
          <div className="md:col-span-2">
            <Label className="text-xs">Custom option name</Label>
            <Input
              value={opt.display_name_override || ''}
              onChange={(e) => onUpdate({ display_name_override: e.target.value })}
              placeholder="e.g. Peri Peri"
            />
          </div>
        )}
        {source.kind === 'categories' && (
          <div>
            <Label className="text-xs">Override for menu product (optional)</Label>
            <Select
              value={opt.source_ref || ''}
              onValueChange={(v) => onUpdate({ source_ref: v })}
            >
              <SelectTrigger><SelectValue placeholder="Any in category" /></SelectTrigger>
              <SelectContent>
                {menuItems.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label className="text-xs">Pricing mode</Label>
          <Select value={opt.pricing_mode} onValueChange={(v) => onUpdate({ pricing_mode: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {PRICING_MODES.map((m) => (
                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {['fixed_surcharge', 'fixed_discount'].includes(opt.pricing_mode) && (
          <div>
            <Label className="text-xs">Amount (£)</Label>
            <Input
              type="number"
              step="0.01"
              min={0}
              value={opt.surcharge}
              onChange={(e) => onUpdate({ surcharge: Math.max(0, Number(e.target.value) || 0) })}
            />
          </div>
        )}
        {opt.pricing_mode === 'above_allowance' && (
          <div>
            <Label className="text-xs">Allowance (£)</Label>
            <Input
              type="number"
              step="0.01"
              min={0}
              value={opt.allowance}
              onChange={(e) => onUpdate({ allowance: Math.max(0, Number(e.target.value) || 0) })}
            />
          </div>
        )}
        <div className="md:col-span-3">
          <Label className="text-xs">Display name override (optional)</Label>
          <Input
            value={opt.display_name_override || ''}
            onChange={(e) => onUpdate({ display_name_override: e.target.value || null })}
            placeholder="Leave blank to use the product name"
          />
        </div>
        <div className="md:col-span-3">
          <Label className="text-xs">Nested modifier groups shown after this pick</Label>
          <div className="flex flex-wrap gap-1 mt-1">
            {modifierGroups.map((mg) => {
              const on = (opt.nested_modifier_group_ids || []).includes(mg.id);
              return (
                <Button
                  key={mg.id}
                  type="button"
                  size="sm"
                  variant={on ? 'default' : 'outline'}
                  className="rounded-full text-xs"
                  onClick={() => {
                    const next = new Set(opt.nested_modifier_group_ids || []);
                    if (on) next.delete(mg.id);
                    else next.add(mg.id);
                    onUpdate({ nested_modifier_group_ids: Array.from(next) });
                  }}
                >
                  {mg.title || mg.name}
                </Button>
              );
            })}
            {modifierGroups.length === 0 && (
              <p className="text-xs text-muted-foreground">
                No modifier groups yet — create them in Admin → Modifier Groups.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// -------------------------------------------------------------------------
// Bulk product picker dialog
// -------------------------------------------------------------------------
const BulkProductPicker = ({ menuItems, already, onClose, onAdd }) => {
  const [picked, setPicked] = useState([]);
  const [mode, setMode] = useState('included');
  const [surcharge, setSurcharge] = useState(0);
  const [query, setQuery] = useState('');
  const filtered = menuItems.filter((m) =>
    m.name.toLowerCase().includes(query.toLowerCase()),
  );
  const toggle = (id) => {
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="p-4 border-b flex items-center justify-between">
          <h4 className="font-semibold">Add products as options</h4>
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
        </div>
        <div className="p-4 space-y-3 border-b">
          <Input placeholder="Search products…" value={query} onChange={(e) => setQuery(e.target.value)} />
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Label className="text-xs">Default pricing mode</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRICING_MODES.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {['fixed_surcharge', 'fixed_discount'].includes(mode) && (
              <div className="w-32">
                <Label className="text-xs">Amount (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min={0}
                  value={surcharge}
                  onChange={(e) => setSurcharge(Math.max(0, Number(e.target.value) || 0))}
                />
              </div>
            )}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-1" data-testid="bulk-add-list">
          {filtered.map((m) => {
            const on = picked.includes(m.id);
            const disabled = already.has(m.id);
            return (
              <label
                key={m.id}
                className={`flex items-center gap-2 p-2 rounded-md cursor-pointer ${disabled ? 'opacity-40' : 'hover:bg-gray-50'}`}
              >
                <input
                  type="checkbox"
                  checked={on}
                  disabled={disabled}
                  onChange={() => toggle(m.id)}
                />
                <span className="flex-1 text-sm">{m.name}</span>
                <span className="text-xs text-muted-foreground">£{Number(m.price).toFixed(2)}</span>
                {m.category && <span className="text-[10px] text-muted-foreground">{m.category}</span>}
              </label>
            );
          })}
        </div>
        <div className="p-3 border-t flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={picked.length === 0}
            onClick={() => onAdd(picked, mode, surcharge)}
            data-testid="bulk-add-confirm"
          >
            Add {picked.length} product{picked.length === 1 ? '' : 's'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BundleConfigEditor;
